import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import cookie from 'js-cookie';
import get from 'lodash/get';
import queryString from 'query-string';
import {
  parsePhoneNumber,
  formatPhoneNumber
} from 'react-phone-number-input';

import {
  STYLEQUIZ_STATUS,
  PM_STATUS,
  MOODBOARD_STATUS,
  SPACE_STATUS,
  TAT_V3,
  sections,
  IMAGE_SIZES,
  LAYOUT_ID_OFFSET,
  LAYOUTS_MAX
} from 'utils/constants';

export const toOrdinal = (cardinal) => {
  const ordinals = [
    'zeroth',
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth'
  ];
  if (cardinal < 0 || cardinal + 1 > ordinals.length) {
    return '';
  }
  return ordinals[cardinal];
};

export const dataURItoBlob = (dataURI) => {
  let byteString,
    mimeString;

  if (dataURI.split(',')[0].indexOf('base64') !== -1) {
    byteString = window.atob(dataURI.split(',')[1]);
  } else {
    byteString = decodeURI(dataURI.split(',')[1]);
  }

  mimeString = dataURI
    .split(',')[0]
    .split(':')[1]
    .split(';')[0];

  const content = [];
  for (let i = 0; i < byteString.length; i++) {
    content[i] = byteString.charCodeAt(i);
  }

  return new window.Blob([new Uint8Array(content)], { type: mimeString });
};

export const getStatus = (space) => {
  const { dashboard: { stylequiz, moodboard, pm: { phStatus } } } = space;
  const { styleResult } = stylequiz;
  const styleQuizCompleted =
    get(stylequiz, 'status') === STYLEQUIZ_STATUS.COMPLETED;

  const pmSubmitted = phStatus !== PM_STATUS.NOT_STARTED;
  const pmPending = phStatus === PM_STATUS.PENDING;
  const pmApproved = phStatus === PM_STATUS.APPROVED;

  const moodboardSubmitted = moodboard === MOODBOARD_STATUS.SUBMITTED;
  return {
    styleQuizCompleted,
    pmSubmitted,
    moodboardSubmitted,
    styleResult,
    pmPending,
    pmApproved
  };
};

export const isValidDate = (inputDate) => {
  return inputDate instanceof Date && !isNaN(inputDate.valueOf());
};

export const getDateInWords = (inputDate) => {
  if (!isValidDate(inputDate)) {
    return '';
  }

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const totalDays = new Date(inputDate.getYear(), inputDate.getMonth() + 1, 0).getDate();
  const dayFraction = inputDate.getDate() / totalDays;

  let monthPrefix;
  if (dayFraction > 0.66) {
    monthPrefix = 'late';
  } else if (dayFraction > 0.33) {
    monthPrefix = 'mid';
  } else {
    monthPrefix = 'early';
  }

  return `${monthPrefix} ${monthNames[inputDate.getMonth()]}`;
};

export const getEta = (space) => {
  const currentDate = new Date();
  const dateFormat = 'ddd MM/DD by hh:mm A';
  if (!space || !space.progressInfo) {
    return format(currentDate.setDate(currentDate.getDate() + 10), dateFormat);
  }
  return format(space.progressInfo.eta, dateFormat);
};

export const setUTMCookie = (
  // the URL query string with possible UTM query params
  search,

  // The function used to set a cookie string by name.
  // Normally the default cookie.set function from js-cookie.
  setCookie = cookie.set
) => {
  const query = queryString.parse(search);

  // UTM data present
  let UTMString = '';
  Object.keys(query).forEach((key) => {
    if (key.startsWith('utm')) {
      UTMString += `${key}=${query[key]}&`;
    }
  });
  if (UTMString) {
    const in7days = 7;
    setCookie('utm_string', UTMString.slice(0, -1), { expires: in7days });
  }
};

export const getUTMParamsFromCookie = (
  // The function used to look up a cookie string by name.
  // Normally the default cookie.get function from js-cookie.
  getCookie = cookie.get
) => {
  const UTMData = {};
  const UTMString = getCookie('utm_string');
  if (UTMString) {
    UTMString.split('&').forEach(UTMElem => {
      if (UTMElem.startsWith('utm')) {
        const keyValue = UTMElem.split('=');
        UTMData[keyValue[0]] = keyValue[1];
      }
    });
  }
  return UTMData;
};

export const deepCloneObject = (obj) => {
  // this looks silly buts it's an easy way to deep copy and apparently is also fairly performant
  return JSON.parse(JSON.stringify(obj));
};

/** percent as a floating point number between 0 and 1 */
export const getScrollPercent = (element) => {
  if (!element) { return 0; }

  return element.scrollTop / (element.scrollHeight - element.clientHeight);
};

// given a reference element scroll to bottom of element
export const scrollToBottom = (ref) => {
  if (ref && ref.current) {
    ref.current.scrollTop = ref.current.scrollHeight;
  }
};

// checking equality by value
export const areArraysEqual = (arr1, arr2) => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    console.error('One or both arguments to areArraysEqual() are not an Array');
    return false;
  }

  if ((arr1 && !arr2) || ((!arr1 && arr2))) { return false; }

  if (!arr1 && !arr2) { return true; }

  if (arr1.length !== arr2.length) { return false; }

  for (var i = 0, l = arr1.length; i < l; i++) {
    // Check if we have nested arrays
    if (Array.isArray(arr1[i]) && Array.isArray(arr2[i])) {
      // recurse into the nested arrays
      if (!areArraysEqual(arr1[i], arr2[i])) { return false; }
    } else if ((arr1[i] instanceof Object) && (arr2[i] instanceof Object)) {
      if (!areObjectsEqual(arr1[i], arr2[i])) { return false; }
    } else if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
};

// checking equality by value
export const areObjectsEqual = (obj1, obj2) => {
  if (!(obj1 instanceof Object) || !(obj2 instanceof Object)) {
    console.error('One or both arguments to areObjectsEqual() are not an Object');
    return false;
  }

  if ((obj1 && !obj2) || (!obj1 && obj2)) { return false; }

  if (!obj1 && !obj2) { return true; }

  // For the first loop, we only check for types
  for (let propName in obj1) {
    // Check for inherited methods and properties - like .equals itself
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/hasOwnProperty
    // Return false if the return value is different
    if (obj1.hasOwnProperty(propName) !== obj2.hasOwnProperty(propName)) {
      return false;
    } else if (typeof obj1[propName] !== typeof obj2[propName]) {
      // Check instance type
      // Different types => not equal
      return false;
    }
  }
  // Now a deeper check using other objects property names
  for (let propName in obj2) {
    // We must check instances anyway, there may be a property that only exists in obj2
    if (obj1.hasOwnProperty(propName) !== obj2.hasOwnProperty(propName)) {
      return false;
    } else if (typeof obj1[propName] !== typeof obj2[propName]) {
      return false;
    }
    // If the property is inherited, do not check any more (it must be equa if both objects inherit it)
    if (!obj1.hasOwnProperty(propName)) { continue; }

    // Now the detail check and recursion

    // This returns the script back to the array comparing
    if (Array.isArray(obj1[propName]) && Array.isArray(obj2[propName])) {
      // recurse into the nested arrays
      if (!areArraysEqual(obj1[propName], obj2[propName])) { return false; }
    } else if ((obj1[propName] instanceof Object) && (obj2[propName] instanceof Object)) {
      // recurse into another objects
      if (!areObjectsEqual(obj1[propName], obj2[propName])) { return false; }
    } else if (obj1[propName] !== obj2[propName]) {
      // Normal value comparison for strings and numbers
      return false;
    }
  }
  // If everything passed, let's say YES
  return true;
};

export const styleboardBeingProcessed = (styleboardState) => {
  // Get a Styleboard's state and return a boolean indicating if the styleboard
  // is in one of the states in which it's being processed by Modsy.
  const processingStates = [SPACE_STATUS.IN_PROGRESS, SPACE_STATUS.REVIEW, SPACE_STATUS.DESIGNS_PENDING];
  return processingStates.includes(styleboardState);
};

// Finds text size as rendered assuming no other size-affecting styles are applied to it
export const computeTextWidth = (font, text) => {
  let f = font || '12px arial';

  const el = document.createElement('div');
  el.textContent = text;
  el.style.position = 'absolute';
  el.style.float = 'left';
  el.style['white-space'] = 'nowrap';
  el.style.visibility = 'hidden';
  el.style.font = f;

  document.body.appendChild(el);
  const width = el.getBoundingClientRect().width;
  document.body.removeChild(el);

  return width;
};

export const getTATForPackageTypes = (packageTypes) => {
  let packageTAT = '6-8 days'; // default if we don't have a matching package type
  if (!packageTypes || !packageTypes.length || packageTypes.length < 1) { return packageTAT; }

  let packageSku = packageTypes[0];
  if (TAT_V3[packageSku]) { packageTAT = TAT_V3[packageSku]; }

  return packageTAT;
};

/**
 * Replaces enter and html breaks in a given string
 * with newline characters.
 */
export const formatBreaks = (str) => {
  if (str) {
    return str.replace(/(<br>|↵)/g, '\n');
  }
  return '';
};

export const toTitleCase = (str) => {
  return str.replace(
    /\w\S*/g,
    function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
};

/**
 * Shuffles / Randomizes array elements in place.
 */
export const shuffle = (array, startIndex, endIndex) => {
  for (let i = endIndex; i > startIndex; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
};

/**
 * Safe version of window.performance.now() browser API
 */
export const now = () => {
  if (typeof window.performance !== 'undefined' && window.performance.now) {
    return window.performance.now();
  }

  return Date.now();
};

/**
 * Wait for the specified milliseconds.
 *
 * Example: wait 5 seconds
 *   await waitMilliseconds(5000);
 */
export const waitMilliseconds = (milliseconds) => new Promise(resolve => {
  setTimeout(() => { resolve(); }, milliseconds);
});

/**
 * Preloads an image.
 * Our goal here to get the image cached by the browser for later use.
 * Thus, this only works if the image has cache control tags set.
 */
export const preLoadImage = (url) => {
  return new Promise(
    (resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve();
      img.src = url;
    }
  ).then(() => {}).catch(() => {});
};

/**
 * Peformance tuning helper function.
 *
 * Assign component's shouldComponentUpdate lifecycle
 * method to this function and it will print out which
 * prop or state change caused a re-render.
 *
 * Example:
 * shouldComponentUpdate (nextProps, nextState) {
 *  return whyDidComponentUpdate(nextProps, nextState, this.props, this.state);
 * }
 *
 * NOT INTENDED FOR PRODUCTION USE, ONLY FOR DEBUGGING.
 */
export const whyDidComponentUpdate = (nextProps, nextState, props, state) => {
  const findWhatChanged = (next, curr) => {
    const varChanged = next && curr && Object.entries(curr).find(([key, curVal]) => (curVal !== next[key]));
    return varChanged;
  };

  const propVarChanged = findWhatChanged(nextProps, props);
  const stateVarChanged = findWhatChanged(nextState, state);

  const shouldUpdate = !!(propVarChanged || stateVarChanged);
  if (shouldUpdate) {
    if (propVarChanged) {
      console.log(`prop: ${propVarChanged[0]}`, propVarChanged[1], nextProps[propVarChanged[0]]);
    }
    if (stateVarChanged) {
      console.log(`state: ${stateVarChanged[0]}`, stateVarChanged[1], nextState[stateVarChanged[0]]);
    }
  }

  return shouldUpdate;
};

/**
 * Equivalent of the `whyDidComponentUpdate` above but using hooks
 * https://usehooks.com/useWhyDidYouUpdate/
 */
export function useWhyDidYouUpdate (name, props) {
  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  const previousProps = useRef();

  useEffect(() => {
    if (previousProps.current) {
      // Get all keys from previous and current props
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      // Use this object to keep track of changed props
      const changesObj = {};
      // Iterate through keys
      allKeys.forEach(key => {
        // If previous is different from current
        if (previousProps.current[key] !== props[key]) {
          // Add to changesObj
          changesObj[key] = {
            from: previousProps.current[key],
            to: props[key]
          };
        }
      });

      // If changesObj not empty then output to console
      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj);
      }
    }

    // Finally update previousProps with current props for next hook call
    previousProps.current = props;
  });
}

// generates two digit string for current month
export const generateMonth = () => {
  const monthInt = new Date().getMonth() + 1;
  return monthInt < 10 ? `0${monthInt}` : `${monthInt}`;
};

export const hasWebGL = () => {
  let supported;

  try {
    var canvas = document.createElement('canvas');
    supported =
      !!window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    if (supported) {
      supported = true;
    }
  } catch (e) {
    supported = false;
  }

  if (!supported) {
    console.error('WebGL is not supported');
  }

  canvas = undefined;

  return supported;
};

// Validates shipping and payment information
export const validateForm = (form, same, fromWhichCart) => {
  const needBillingAndCCInfo = !form.selectedCC && form.selectedPaymentMethod === 'cc';

  const fromUSA = form.billingCountry === 'US';
  if (fromWhichCart !== 'packageCart') {
    if (form.shippingAddressName.length < 1) {
      return { section: sections.Shipping, field: 'Name' };
    } else if (form.shippingStreetAddress.length < 1) {
      return { section: sections.Shipping, field: 'Street Address' };
    } else if (form.shippingCity.length < 1) {
      return { section: sections.Shipping, field: 'City' };
    } else if (
      fromUSA &&
      (form.shippingState === 'State' || form.shippingState.length < 1)
    ) {
      return { section: sections.Shipping, field: 'State' };
    } else if (form.shippingZipcode.length < 1) {
      return { section: sections.Shipping, field: 'Zipcode' };
    } else if (
      !form.shippingPhoneNumber ||
      (form.shippingPhoneNumber && form.shippingPhoneNumber.length < 6)
    ) {
      return { section: sections.Shipping, field: 'Phone Number' };
    }
  }

  if ((!same || fromWhichCart === 'packageCart') && needBillingAndCCInfo) {
    if (form.billingStreetAddress.length < 1) {
      return {
        section: sections.Billing,
        field: 'Street Address',
        customError: 'Error: Missing Street Address'
      };
    } else if (form.billingCity.length < 1) {
      return {
        section: sections.Billing,
        field: 'City',
        customError: 'Error: Missing City'
      };
    } else if (
      fromUSA &&
      (form.billingState === 'State' || form.billingState.length < 1)
    ) {
      return {
        section: sections.Billing,
        field: 'State',
        customError: 'Error: Missing State'
      };
    } else if (form.billingZipcode.length < 1) {
      return {
        section: sections.Billing,
        field: 'Zipcode',
        customError: 'Error: Missing Zipcode'
      };
    }
  }

  if (form.cardName.length < 1 && needBillingAndCCInfo) {
    return { section: sections.Payment, field: 'Name' };
  }

  if (needBillingAndCCInfo && !window.Stripe.validateCardNumber(form.cardNumber)) {
    return {
      section: sections.Payment,
      field: 'Card Number',
      customError: 'Error: Card number is not valid.'
    };
  } else if (needBillingAndCCInfo && !window.Stripe.validateExpiry(form.expireMonth, form.expireYear)) {
    return {
      section: sections.Payment,
      field: 'Expire Date',
      customError: 'Error: Card has expired.'
    };
  } else if (needBillingAndCCInfo && !window.Stripe.validateCVC(form.cvc)) {
    return {
      section: sections.Payment,
      field: 'CVC',
      customError: 'Error: CVC is not valid.'
    };
  }

  return null;
};

export const getExt = filename => {
  const re = /(?:\.([^.]+))?$/;
  return re.exec(filename)[0];
};

export const getImage = (image, size) => {
  const ext = getExt(image);
  if (ext && size) {
    return image.replace(/(?:\.([^.]+))?$/, `${IMAGE_SIZES[size]}${ext}`);
  }
  return image;
};

/**
 * Normalizes ProductSearch filter responses for PB Views.
 *
 * NOTE: updatedFilterUiEnabled argument should be removed when AlternativesFilters feature flag is removed.
 */
export const normalizeFilters = (searchFilters, enabledFilters, updatedFilterUiEnabled = false) => {
  const CATALOG_FILTER_KEY = 'Show3DAsset';
  const SIZE_FILTER_KEYS = ['Width', 'Height', 'Depth'];
  const BACKORDER = 'Backorder';
  const IN_STOCK = 'In stock';
  const OUT_OF_STOCK = 'Currently Unavailable';
  const MADE_TO_ORDER = 'Made to order';

  const AVAILABILITY_VALUE_MAPPING = {
    '-4': BACKORDER,
    '2': IN_STOCK,
    '3': OUT_OF_STOCK,
    '-6': MADE_TO_ORDER
  };

  const filters = searchFilters.map(filter => {
    return filter.label();
  });

  let filterOptions = {};
  searchFilters.forEach(filter => {
    filterOptions[filter.label()] = filter.values().map(value => ({ name: value }));
  });

  let filterValues = {};
  searchFilters.forEach(filter => {
    filterValues[filter.label()] = [];
  });
  Object.keys(enabledFilters).forEach(label => {
    filterValues[label] = updatedFilterUiEnabled ? enabledFilters[label] : enabledFilters[label].map(value => ({ name: value }));
  });

  // This is a temporary hack.
  // The backend returns numeric values for Availability. We need to convert that value
  // here for the UI. If the value was set local to the FE, we don't use the mapping.
  // The backend should be converted to send the mapped version, as that is what it expects
  // from the FE. Elsie will need to be updated to match when that api change happens.
  if (filterValues['Availability']) {
    filterValues['Availability'] = filterValues['Availability'].map((val) => {
      return AVAILABILITY_VALUE_MAPPING[val] ? AVAILABILITY_VALUE_MAPPING[val] : val;
    });
  }

  let filtersAppliedCnt = 0;
  let sizeCounted = false;
  Object.keys(enabledFilters).forEach(label => {
    switch (true) {
      case label === CATALOG_FILTER_KEY:
      case SIZE_FILTER_KEYS.includes(label) && sizeCounted:
        break;
      case SIZE_FILTER_KEYS.includes(label):
        filtersAppliedCnt = enabledFilters[label] === undefined ? filtersAppliedCnt : filtersAppliedCnt + 1;
        sizeCounted = true;
        break;
      default:
        filtersAppliedCnt += enabledFilters[label].length;
        break;
    }
  });

  return { filters, filtersAppliedCnt, filterOptions, filterValues };
};

/**
 * Regular expression for determining whether or not an email is valid.
 */
export const isValidEmail = (email) => {
  // eslint-disable-next-line
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

/**
 * memoizer methods for cases where createSelector and useMemo aren't appropriate
 * (async and not in a pure component)
 * @param {Function} method
 */
export const memoizeAsync = (method) => {
  let cache = {};

  return async function () {
    let args = JSON.stringify(arguments);
    cache[args] = cache[args] || method.apply(this, arguments);
    return cache[args];
  };
};

/**
 * synchronous memoizer for non-redux, non-pure component methods
 * @param {Function} method
 */
export const memoize = (method) => {
  let cache = {};

  return function () {
    let args = JSON.stringify(arguments);
    cache[args] = cache[args] || method.apply(this, arguments);
    return cache[args];
  };
};

/**
 * CART
 */

export const isAddressName = (field) => field === 'addressName';

export const isStreetAddress = (field) => field === 'streetAddress';

export const getFirstName = (fullName) => {
  return fullName.trim().split(' ')[0];
};

export const getLastName = (fullname) => {
  return fullname.trim().split(' ').slice(-1).join(' ');
};

export const hasFirstAndLastName = (fullname) => fullname.trim().indexOf(' ') !== -1;

export const isMinNameLength = (lastName) => lastName.length >= 2;

export const validateAddressName = (fullname) => {
  const lastName = getLastName(fullname);

  const containsNumber = lastName.match(/\d+/g) != null;
  const containsPeriod = lastName.match(/\./g) != null;

  if (containsNumber || containsPeriod) {
    return false;
  }

  return true;
};

export const validateStreetAddress = (street) => {
  const containsPound = street.match(/#/g) != null;

  if (containsPound) {
    return false;
  }

  return true;
};

export const CART_FORM_ERRORS = {
  ADDRESS_FORM: {
    FIRST_LAST: 'Error: Must enter both a first and last name',
    MIN_CHARS: 'Error: Last name must be at least two characters'
  },
  CARD_FORM: {
    FIRST_LAST: 'Must enter both a first and last name.',
    MIN_CHARS: 'Last name must be at least two characters.'
  }
};

/**
 * This functions returns a copy of the given string after removing all
 * characters except letters (upper or lower case) and spaces
 */
export const filterNumbersAndSpecialChars = (str) => {
  return str.replace(/[^a-z ]/gi, '');
};

/**
 * ForEach that waits for async callback functions
 * @param {Array} array
 * @param {function} callback
 */
export async function asyncForEach (array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
}

/**
 * Converts from feet, inches, meters, centimeters, to milimeters
 * @param {String} measurementUnit ft if using feet and inches, mt if using meters
 * @param {Number} unitOne the primary unit (feet or meters)
 * @param {Number} unitTwo the secondary unit (inches or centimeters)
 */
export const convertUnits = (measurementUnit, unitOne, unitTwo) => {
  let total = unitOne;
  if (measurementUnit === 'ft') {
    total = unitOne * 304.8;
    if (unitTwo) {
      unitTwo *= 25.4;
      total += unitTwo;
    }
  } else if (measurementUnit === 'in') {
    total = unitOne * 25.4;
  } else if (measurementUnit === 'mt') {
    total = unitOne * 1000;
    if (unitTwo) {
      unitTwo *= 10;
      total += unitTwo;
    }
  }

  return total;
};

/**
 * Checks if a string is a valid data url
 * @param {String} url
 */
export const isDataURL = (url) => {
  // eslint-disable-next-line
  const regex = /^\s*data:([a-z]+\/[a-z0-9-+.]+(;[a-z-]+=[a-z0-9-]+)?)?(;base64)?,([a-z0-9!$&',()*+;=\-._~:@\/?%\s]*)\s*$/i;
  return !!url.match(regex);
};

/**
 * Requests device orientation permission for iOS 13+
 * returns a promise that responds with the permission state
 * or false if the device is non iOS 13+
 * NOTE: this is required for the deviceorientation event to work on iOS 13+
 * AND must be called from a user-driven event like a button click
 */
export const requestDeviceOrientationPermission = (isMobile) => {
  // DeviceOrientationEvent doesn't exist for safari
  // eslint-disable-next-line
  if(!window.DeviceOrientationEvent)
    return Promise.resolve('granted');

  // eslint-disable-next-line
  if (isMobile && typeof DeviceOrientationEvent.requestPermission === 'function') {
    // eslint-disable-next-line
    return DeviceOrientationEvent.requestPermission().then(response => {
      return response;
    }).catch(error => { return error; });
  } else {
    // handle regular non iOS 13+ devices
    return Promise.resolve('granted');
  }
};

/**
 * Requests device motion permission for iOS 13+
 * returns a promise that responds with the permission state
 * or false if the device is non iOS 13+
 * NOTE: this is required for the devicemotion event to work on iOS 13+
 * AND must be called from a user-driven event like a button click
 */
export const requestDeviceMotionPermission = (isMobile) => {
  // eslint-disable-next-line
  if(!window.DeviceMotionEvent)
    return Promise.resolve('granted');

  // eslint-disable-next-line
  if (isMobile && typeof DeviceMotionEvent.requestPermission === 'function') {
    // eslint-disable-next-line
    return DeviceMotionEvent.requestPermission().then(response => {
      return response;
    }).catch(error => { return error; });
  } else {
    // handle regular non iOS 13+ devices
    return Promise.resolve('granted');
  }
};

/**
 * Hook to store reference to previous prop value
 * Copied from https://usehooks.com/usePrevious/ with extra
 * return callback to fetch last changed values
 */
export const usePrevious = (values = []) => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef([]);

  // Store current values in ref
  useEffect(() => {
    ref.current = values;
  }, [...values]); // Only re-run if values change

  const previousValues = ref.current;

  const getPreviousChanges = () => {
    // sanity check
    if (previousValues.length !== values.length) {
      return values;
    }
    return values.map((value, index) => (value !== previousValues[index]));
  };

  // Return previous value (happens before update in useEffect above) and shallow
  // modified values
  return [previousValues, getPreviousChanges];
};

export const getURLParams = ({
  url = '',
  errorHandler = () => {}
}) => {
  try {
    // eslint-disable-next-line
    const parsedURL = new URL(url);
    const { search } = parsedURL;
    return queryString.parse(search);
  } catch (e) {
    errorHandler(e);
  }
  return null;
};

export const extractPage = ({
  url = '',
  pageParam = 'page',
  errorHandler = () => {},
  defaultPage = null
}) => {
  if (!url) {
    return null;
  }
  const urlParams = getURLParams({ url, errorHandler }) || {};
  return parseInt(get(urlParams, pageParam, null), 10) || defaultPage;
};

/**
 * Sort designs with special case for Layout designs first
 * @param {Design} a
 * @param {Design} b
 */
export const sortDesigns = (a, b) => {
  let aIndex = a.designIndex();
  let bIndex = b.designIndex();

  // offset layout indices so that they are negative
  // this makes them render at the bottom of the list
  // even though their indices are the highest
  if (a.isLayout()) {
    aIndex = -(a.designIndex() - LAYOUT_ID_OFFSET + LAYOUTS_MAX);
  }
  if (b.isLayout()) {
    bIndex = -(b.designIndex() - LAYOUT_ID_OFFSET + LAYOUTS_MAX);
  }

  return bIndex - aIndex;
};

export const getFormattedPhoneNumber = (phoneNumberString) => {
  const phoneNumber = parsePhoneNumber(phoneNumberString);

  const isUSPhone = phoneNumber.country === 'US';

  if (isUSPhone && phoneNumberString) {
    return `+1 ${formatPhoneNumber(phoneNumber, 'National')}`;
  }
  return formatPhoneNumber(phoneNumber, 'International');
};

export const getRelativeBoundingRect = (element, parent) => {
  if (!element || !parent) { return null; }

  const elRect = element.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();

  return {
    top: elRect.top - parentRect.top,
    right: elRect.right - parentRect.right,
    bottom: elRect.bottom - parentRect.bottom,
    left: elRect.left - parentRect.left
  };
};

export const getCurrentCustomPropPriceTiers = (customPropsAvailability) => {
  if (!customPropsAvailability) { return null; }

  let exactPieceTier = customPropsAvailability.exactPiecePriceTiers[0];
  let standInTier = customPropsAvailability.standInPriceTiers[0];
  for (let i = 0; i < customPropsAvailability.exactPiecePriceTiers.length; ++i) {
    // skip tiers that have been used up, get the first tier that still has some available
    if (customPropsAvailability.exactPiecePriceTiers[i].used < customPropsAvailability.exactPiecePriceTiers[i].limit) {
      exactPieceTier = customPropsAvailability.exactPiecePriceTiers[i];
      break;
    }
  }

  for (let i = 0; i < customPropsAvailability.standInPriceTiers.length; ++i) {
    // skip tiers that have been used up, get the first tier that still has some available
    if (customPropsAvailability.standInPriceTiers[i].used < customPropsAvailability.standInPriceTiers[i].limit) {
      standInTier = customPropsAvailability.standInPriceTiers[i];
      break;
    }
  }

  return { exactPieceTier, standInTier };
};
